import {
  toWSDateString,
  WSAlert,
  WSButton,
  WSDivider,
  WSElement,
  WSEmptyState,
  WSFlexBox,
  WSGrid,
  WSList,
  WSPill
} from "@wingspanhq/fe-component-library";
import { IContractorPayoutsSummaryReportResponse } from "@wingspanhq/payments/dist/interfaces/api/reports";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useUserId } from "../../../../query/hooks/helpers";
import { IContractorPayoutsSummaryReportRequest } from "../../../../query/payments/queries";
import { paymentsService } from "../../../../services/payments";
import { HeroRangeSelect } from "../../../../shared/components/HeroRangeSelect/HeroRangeSelect";
import { VerticalDivider } from "../../../../shared/components/VerticalDivider";
import { analyticsService } from "@services/analyticsService";
import { useAccountAccess } from "../../../../v3/hooks/useAccountAccess";
import { CardReportLoading } from "../../components/CardReportLoading/CardReportLoading";
import { DownloadReportButton } from "../../components/DownloadReportButton/DownloadReportButton";
import { useSelectColumnsModal } from "../../components/ModalSelectColumns";
import { ReportsTable } from "../../components/ReportsTable/ReportsTable";
import { useFetchReportData } from "../../hooks/useFetchReportData";
import { useReportColumns } from "../../hooks/useReportColumns";
import { ReportFormat, ReportType } from "../../types";
import { formatReportsValue } from "../../utils/formatReportsValue";
import { selectorColumnsWithAdditionalData } from "../../utils/selectorColumnsWithAdditionalData";
import { selectorTableColumns } from "../../utils/selectorTableColumns";
import { getColumns } from "./columns";
import { CustomField } from "@wingspanhq/wingspan-api-sdk";

const COLUMNS_STORAGE_KEY =
  "REPORTS_COLLABORATOR_PAYABLES_SUMMARY_COLUMNS_STORAGE_KEY";

export const CollaboratorPayablesSummaryDashboard: React.FC<{
  collaboratorsAdditionalData: CustomField[];
}> = ({ collaboratorsAdditionalData }) => {
  const { hasFullAccess } = useAccountAccess();
  const userId = useUserId();
  const [selectedDateRange, setSelectedDateRange] = useState<[Date, Date]>([
    new Date(new Date().getFullYear(), 0, 1, 0, 0, 0, 0),
    new Date(new Date().getFullYear(), 11, 31, 23, 59, 59, 999)
  ]);
  const [showCompletionAlert, setShowCompletionAlert] = useState(false);

  const columns =
    selectorColumnsWithAdditionalData<IContractorPayoutsSummaryReportResponse>(
      getColumns(hasFullAccess),
      collaboratorsAdditionalData,
      { prefixGroupName: false }
    );

  const getColumnByKey = (key: string) =>
    columns.find(_column => key === _column.value)!;

  const [selectedColumns, setSelectedColumns] = useReportColumns(
    COLUMNS_STORAGE_KEY,
    userId,
    columns
  );

  const modalSelectColumns = useSelectColumnsModal();

  const selectedRangeKey = selectedDateRange
    .map(d => toWSDateString(d, "monthDayYear"))
    .join(" - ");

  const isRunButtonDisabled = !selectedDateRange || !selectedColumns.length;

  const filters: IContractorPayoutsSummaryReportRequest = useMemo(
    () => ({
      paidFromDate: selectedDateRange[0],
      paidToDate: selectedDateRange[1]
    }),
    [selectedDateRange]
  );

  const request = useCallback(
    async (pageSize, pageNumber) => {
      return await paymentsService.reports.collaboratorPayable(filters, {
        page: {
          size: pageSize,
          number: pageNumber
        }
      });
    },
    [filters]
  );

  const { query, totalRowsCount, processedRowsCount } = useFetchReportData<
    IContractorPayoutsSummaryReportResponse,
    {
      summary: { listSize: number };
      data: IContractorPayoutsSummaryReportResponse[];
    }
  >(["QUERY_COLLABORATOR_PAYABLES_SUMMARY", filters], request);

  // Check if report has just completed
  React.useEffect(() => {
    if (
      !query.isFetching &&
      totalRowsCount > 0 &&
      processedRowsCount === totalRowsCount
    ) {
      setShowCompletionAlert(true);
    }
  }, [processedRowsCount, query.isFetching, totalRowsCount]);

  useEffect(() => {
    if (query.isFetching) {
      setShowCompletionAlert(false);
    }
  }, [query.isFetching]);

  useEffect(() => {
    setShowCompletionAlert(false);
  }, [selectedDateRange]);

  const tableData = query.data || [];
  const isDownloadButtonDisabled = tableData.length === 0 || query.isFetching;

  const getReportJSON = () => {
    if (!tableData.length) {
      return [];
    }
    return [
      selectedColumns.map(getColumnByKey).map(c => c?.label),
      ...tableData
        .map(data => {
          return selectedColumns.map(column => {
            let result;
            const currentColumn = getColumnByKey(column);

            try {
              result = formatReportsValue(currentColumn?.getTableCell?.(data));
            } catch (e) {
              console.error(`REPORT ERROR! "${currentColumn?.label}": `, e);
              result = undefined;
            }

            return result;
          });
        })
        .filter(arr => arr.some(Boolean))
    ];
  };

  return (
    <>
      <WSDivider mb="M" />
      <WSGrid>
        <WSGrid.Item span={{ s: "12" }}>
          <WSFlexBox.CenterY>
            <WSElement>
              <HeroRangeSelect
                color="blue500"
                position={"right"}
                customLabel={selectedRangeKey}
                onChange={data => {
                  setSelectedDateRange(data.value);
                }}
                value={selectedDateRange}
              />
            </WSElement>
            <VerticalDivider mr="XL" />
            <WSFlexBox.CenterY
              onClick={() =>
                modalSelectColumns.open({
                  selectedColumns,
                  columns,
                  onSubmit: ({ columns }) => {
                    setSelectedColumns(columns);
                  }
                })
              }
              data-testid="selectColumns"
            >
              <WSButton.Link icon="menu">Selected columns</WSButton.Link>
              <WSPill ml="M" theme="blue" text={`${selectedColumns.length}`} />
            </WSFlexBox.CenterY>

            <VerticalDivider mx="XL" />
            <WSButton.Primary
              name="runReport"
              disabled={isRunButtonDisabled}
              onAsyncClick={async () => {
                analyticsService.track("Report Ran", {
                  reportName: ReportType.CollaboratorPayablesSummary,
                  selectedColumnsCount: selectedColumns.length,
                  selectedColumns,
                  filters
                });
                await query.refetchData();
              }}
            >
              Run report
            </WSButton.Primary>
          </WSFlexBox.CenterY>
        </WSGrid.Item>
      </WSGrid>
      <WSDivider mt="M" mb="XL" />
      <WSList gap="XL">
        <WSFlexBox.CenterY justify="flex-end">
          <DownloadReportButton
            disabled={isDownloadButtonDisabled}
            getData={getReportJSON}
            onClick={(format: ReportFormat) => {
              analyticsService.track("Report Downloaded", {
                reportName: ReportType.CollaboratorPayablesSummary,
                selectedColumnsCount: selectedColumns.length,
                selectedColumns,
                filters,
                format
              });
            }}
            fileName={`Wingspan Contractor Report`}
            sheetName={selectedRangeKey}
          />
        </WSFlexBox.CenterY>

        {showCompletionAlert && (
          <WSAlert
            theme="success"
            icon="check-circle"
            onDismiss={() => setShowCompletionAlert(false)}
          >
            Report complete
          </WSAlert>
        )}

        {query.isFetching && (
          <CardReportLoading
            processedRowsCount={processedRowsCount}
            totalRowsCount={totalRowsCount}
          />
        )}

        {tableData.length ? (
          <ReportsTable<IContractorPayoutsSummaryReportResponse>
            mb="M"
            columns={selectorTableColumns(
              selectedColumns.map(getColumnByKey).filter(Boolean)
            )}
            tableData={tableData.map((data, i) => ({
              data,
              id: `id_${i}_${data.payeeId}`
            }))}
          />
        ) : (
          <WSEmptyState
            type="documents"
            title="No report to show yet"
            description={
              query.data?.length === 0
                ? "No contractors with payments found in this date range"
                : "Adjust period range then click 'Run report'"
            }
            orientation="horizontal"
            hasBorder
          />
        )}
      </WSList>
    </>
  );
};
